import { useState, useEffect } from "react";
import Typography from "../../components/Typography/Typography";
import EditableText from "./EditableText";
import { Plus, GripVertical, Trash } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import toast, { Toaster } from "react-hot-toast";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

const ChapterSection = ({
  chapter,
  chapterIndex,
  isEditMode,
  updateChapter,
  updateSubchapter,
  addSubchapter,
  removeChapter,
}) => {
  // Function to remove a subchapter
  const removeSubchapter = (subchapterIndex) => {
    if (!chapter || !chapter.subchapters) return;
    
    // Create a copy of the subchapters array
    const updatedSubchapters = [...chapter.subchapters];
    
    // Remove the subchapter at the specified index
    updatedSubchapters.splice(subchapterIndex, 1);
    
    // Update the chapter with the new subchapters array
    updateChapter(chapterIndex, "subchapters", updatedSubchapters);
    
    // Update subchapterItems state to reflect the change
    setSubchapterItems(updatedSubchapters.map((_, idx) => `chapter-${chapterIndex}-sub-${idx}`));
    
    toast.success("Paragrafo rimosso con successo");
  };
  // Create state for subchapter items
  const [subchapterItems, setSubchapterItems] = useState([]);

  // Generate IDs for subchapters whenever chapter changes
  useEffect(() => {
    if (chapter?.subchapters) {
      const newSubchapterItems = chapter.subchapters.map(
        (_, subIdx) => `chapter-${chapterIndex}-sub-${subIdx}`
      );
      setSubchapterItems(newSubchapterItems);
    }
  }, [chapter, chapterIndex]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `chapter-${chapterIndex}`,
    // Add this to prevent distortion
    data: { type: "chapter", chapter },
  });

  const handleRemoveChapter = () => {
    if (typeof removeChapter === "function") {
      removeChapter(chapterIndex);
    } else {
      toast.error("Errore nella rimozione del capitolo");
    }
  };

  const style = {
    transform: CSS.Translate.toString(transform), // Use CSS.Translate instead of CSS.Transform
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
    // Add this to ensure element maintains its original dimensions
    width: isDragging ? "auto" : undefined,
    height: isDragging ? "auto" : undefined,
  };

  // Return null after using hooks
  if (!chapter) return null;

  const handleChapterUpdate = (index, field, newValue) => {
    updateChapter(index, field, newValue);
  };

  const handleSubchapterUpdate = (chapterIdx, subIdx, field, newValue) => {
    updateSubchapter(chapterIdx, subIdx, field, newValue);

    const updatedSubchapters = [...chapter.subchapters];
    updatedSubchapters[subIdx] = {
      ...updatedSubchapters[subIdx],
      [field]: newValue,
    };
  };

  const handleAddSubchapter = (index) => {
    addSubchapter(index);
  };

  // Il numero visualizzato del capitolo (non solo l'indice)
  const displayChapterNumber = chapterIndex + 1;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="p-4 rounded-xl border-2 border-gray-150 bg-white relative"
    >
      <Toaster />
      <div className="flex w-full justify-between items-center pb-4 md:pb-0">
        {isEditMode && (
          <div
            {...attributes}
            {...listeners}
            className="md:absolute md:-left-10 md:top-1/2 md:transform md:-translate-y-1/2 cursor-pointer"
            aria-label="Drag handle"
          >
            <GripVertical
              size={20}
              className="text-gray-300 hover:text-gray-500"
            />
          </div>
        )}

        {isEditMode && (
          <div
            className="md:absolute md:-right-10 md:top-1/2 md:transform md:-translate-y-1/2 cursor-pointer"
            onClick={handleRemoveChapter} // Use the local handler instead of inline function
          >
            <Trash size={20} className="text-error" />
          </div>
        )}
      </div>

      {/* Chapter title and subtitle */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          {isEditMode ? (
            <EditableText
              value={chapter.title}
              onChange={(newValue) =>
                handleChapterUpdate(chapterIndex, "title", newValue)
              }
              className="text-xl font-semibold text-black"
              placeholder="Inserisci un titolo"
              prefix={`${displayChapterNumber}. `}
            />
          ) : (
            <h2 className="text-xl font-semibold text-black">
              {`${displayChapterNumber}. ${chapter.title}`}
            </h2>
          )}
        </div>
      </div>

      {isEditMode ? (
        <EditableText
          value={chapter.subtitle || ""}
          onChange={(newValue) =>
            handleChapterUpdate(chapterIndex, "subtitle", newValue)
          }
          className="text-gray-600 mb-3"
          placeholder="Inserisci un sottotitolo"
        />
      ) : (
        chapter.subtitle && (
          <p className="text-gray-600 mb-3">{chapter.subtitle}</p>
        )
      )}

      {/* Subchapters */}
      {chapter.subchapters && chapter.subchapters.length > 0 && (
        <div
          className={`space-y-2 ${
            isEditMode ? "pl-2" : "pl-6 border-l-2 border-gray-150 ml-2"
          } mt-3`}
        >
          {/* Nested SortableContext for subchapters */}
          <SortableContext 
            items={subchapterItems} 
            strategy={verticalListSortingStrategy}
          >
            {chapter.subchapters.map((sub, subIdx) => (
              <SubchapterItem
                key={`subchapter-${chapterIndex}-${subIdx}`}
                sub={sub}
                subIdx={subIdx}
                chapterIndex={chapterIndex}
                displayChapterNumber={displayChapterNumber}
                isEditMode={isEditMode}
                handleSubchapterUpdate={handleSubchapterUpdate}
                removeSubchapter={removeSubchapter}
              />
            ))}
          </SortableContext>
        </div>
      )}

      {/* Add subchapter button (only visible in edit mode) */}
      {isEditMode && (
        <div className="mt-4">
          <button
            onClick={() => handleAddSubchapter(chapterIndex)}
            className="flex items-center gap-2 text-sm font-medium text-accent"
          >
            <Plus size={16} />
            <Typography size="12" weight="sb" color="accent">
              Aggiungi paragrafo
            </Typography>
          </button>
        </div>
      )}
    </div>
  );
};

const SubchapterItem = ({
  sub,
  subIdx,
  chapterIndex,
  displayChapterNumber,
  isEditMode,
  handleSubchapterUpdate,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `chapter-${chapterIndex}-sub-${subIdx}`,
    // Add data for better drag handling
    data: { type: "subchapter", subchapter: sub },
  });

  // Update the transform to prevent stretching
  const style = {
    transform: CSS.Translate.toString(transform), // Use CSS.Translate instead of CSS.Transform
    transition,
    opacity: isDragging ? 0.5 : 1,
    // Add these to maintain original dimensions
    width: isDragging ? "auto" : undefined,
    height: isDragging ? "auto" : undefined,
  };

  // Numero del sottoparagrafo (subIdx + 1)
  const subchapterNumber = subIdx + 1;

  // Prefisso completo per visualizzazione (es: "2.3 ")
  const displayPrefix = `${displayChapterNumber}.${subchapterNumber} `;

  return (
    <div ref={setNodeRef} style={style} className={`flex gap-2`}>
      {isEditMode && (
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab touch-manipulation"
          aria-label="Drag handle"
        >
          <GripVertical
            size={20}
            className="text-gray-300 hover:text-gray-500"
          />
        </div>
      )}
      <div className="flex-1">
        {isEditMode ? (
          <>
            <EditableText
              value={sub.title}
              onChange={(newValue) =>
                handleSubchapterUpdate(chapterIndex, subIdx, "title", newValue)
              }
              className="text-black font-medium"
              placeholder="Inserisci un titolo per il sottocapitolo"
              prefix={displayPrefix}
            />
            <EditableText
              value={sub.subtitle || ""}
              onChange={(newValue) =>
                handleSubchapterUpdate(
                  chapterIndex,
                  subIdx,
                  "subtitle",
                  newValue
                )
              }
              className="text-gray-600 text-sm"
              placeholder="Inserisci un sottotitolo"
            />
          </>
        ) : (
          <>
            <p className="text-black font-medium">
              {displayPrefix}
              {sub.title}
            </p>
            {sub.subtitle && (
              <p className="text-gray-600 text-sm">{sub.subtitle}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChapterSection;